import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon } from 'lucide-react';
import { BiasCard } from '../components/biases/BiasCard';
import { biasCategories, biases } from '../data/cognitiveBiases';
import { profile } from '../data/profile';
import type { BiasCategory } from '../types/biases';
import { EASE_OUT } from '../utils/motion';
type Filter = 'all' | BiasCategory;
export function CognitiveBiases() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return biases.filter(b => {
      if (filter !== 'all' && b.category !== filter) return false;
      if (!q) return true;
      return [b.name, b.definition, b.strategy, b.example, b.impact].some(t => t.toLowerCase().includes(q));
    });
  }, [query, filter]);
  const countFor = (id: Filter) => id === 'all' ? biases.length : biases.filter(b => b.category === id).length;
  const chips: {
    id: Filter;
    label: string;
    swatch?: string;
  }[] = [{
    id: 'all',
    label: 'All'
  }, ...biasCategories.map(c => ({
    id: c.id,
    label: c.label,
    swatch: c.swatch
  }))];
  return <div className="min-h-screen w-full bg-bias-bg text-bias-text" data-id="element-946" data-mp="2utagt64q40md">
      <header className="mx-auto flex max-w-[1100px] items-center justify-between border-b border-bias-line px-5 py-5 md:px-8" data-id="element-947" data-mp="2uv51qkfzvsqg">
        <Link to={{
        pathname: '/',
        hash: '#playground'
      }} className="group inline-flex items-center gap-2 text-sm font-semibold text-bias-text" data-id="element-948" data-mp="33u07xwj1h2tr">
          <ArrowLeftIcon className="h-4 w-4 text-bias-muted transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-949" data-mp="3ikpprhr09g6l" />
          {profile.name}
        </Link>
        <span className="text-sm text-bias-muted" data-id="element-950" data-mp="3rjuj89npmjbw">Behavioral Design &amp; AI</span>
      </header>

      <main className="mx-auto max-w-[1100px] px-5 pb-24 md:px-8" data-id="element-951" data-mp="3mlt1bs20sq20">
        <section className="py-16 text-center md:py-24" data-id="element-952" data-mp="2k7leftvwjc2o">
          <motion.p initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT
        }} className="text-xs font-semibold uppercase tracking-[0.16em] text-coral" data-id="element-953" data-mp="2k4z81xkqz2m1">
            2026 edition · {biases.length} biases
          </motion.p>
          <motion.h1 initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.05
        }} className="mx-auto mt-5 max-w-3xl text-5xl font-semibold leading-[1.05] tracking-tight md:text-7xl" data-id="element-954" data-mp="32xteu487vof6">
            Cognitive Biases in Product Design
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.1
        }} className="mx-auto mt-6 max-w-md text-base leading-relaxed text-bias-muted" data-id="element-955" data-mp="3guqb4l73ty3g">
            A reference deck for designers, PMs, and researchers. Tap any card to reveal strategy, example, and impact.
          </motion.p>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-bias-muted" data-id="element-956" data-mp="159g28xkjy3nv">
            {biasCategories.map(c => <li key={c.id} className="flex items-center gap-2" data-id="element-957" data-mp="119p7kirwn3e2">
                <span className={`h-2.5 w-2.5 rounded-[3px] ${c.swatch}`} data-id="element-958" data-mp="1xyn0rwk0at94" />
                {c.legend}
              </li>)}
          </ul>
        </section>

        <div className="flex flex-col gap-4 border-b border-bias-line pb-5 lg:flex-row lg:items-center" data-id="element-959" data-mp="1oxov79tx6dof">
          <label className="relative block lg:w-72" data-id="element-960" data-mp="1jym0ojbkfpup">
            <span className="sr-only" data-id="element-961" data-mp="1jy3v2w30jza4">Search biases</span>
            <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-bias-muted" data-id="element-962" data-mp="dj2x6kufh70w" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search biases, strategies…" className="w-full rounded-xl border border-bias-line bg-bias-panel py-2.5 pl-10 pr-4 text-sm text-bias-text placeholder:text-bias-muted focus:border-bias-muted focus:outline-none" data-id="element-963" data-mp="8if7c2hxqw89" />
          </label>
          <div role="radiogroup" aria-label="Filter by category" className="flex flex-wrap gap-2" data-id="element-964" data-mp="1rhuy26a32hx2">
            {chips.map(chip => {
            const active = filter === chip.id;
            return <button key={chip.id} type="button" role="radio" aria-checked={active} onClick={() => setFilter(chip.id)} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ${active ? 'bg-bias-text text-night' : 'bg-bias-panel text-bias-text hover:bg-bias-line'}`} data-id="element-965" data-mp="2k4xwyzio8unu">
                  {chip.swatch && <span className={`h-2 w-2 rounded-full ${chip.swatch}`} data-id="element-966" data-mp="n7a34y66jv93" />}
                  {chip.label}
                  <span className={`text-xs tabular-nums ${active ? 'text-night/60' : 'text-bias-muted'}`} data-id="element-967" data-mp="116sy0p9zda3g">
                    {countFor(chip.id)}
                  </span>
                </button>;
          })}
          </div>
          <p className="text-sm text-bias-muted lg:ml-auto" aria-live="polite" data-id="element-968" data-mp="8j5gxk7x51lk">
            <span className="font-semibold text-bias-text" data-id="element-969" data-mp="3k2co1zrizen">{visible.length}</span> cards
          </p>
        </div>

        {visible.length === 0 ? <div className="py-24 text-center" data-id="element-970" data-mp="ia9x2n9byshz">
            <p className="text-base font-medium" data-id="element-971" data-mp="dh6yg0pp4sux">No biases match “{query}”.</p>
            <button type="button" onClick={() => {
          setQuery('');
          setFilter('all');
        }} className="mt-3 text-sm text-bias-muted underline underline-offset-4 hover:text-bias-text" data-id="element-972" data-mp="3lx0igpj8kmgq">
              Clear search and filters
            </button>
          </div> : <motion.ul layout className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-973" data-mp="1ps14jr57i60i">
            <AnimatePresence initial={false} data-id="element-974" data-mp="1umyldk8q9jny">
              {visible.map(bias => <motion.li key={bias.id} layout initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.2,
            ease: EASE_OUT
          }} data-id="element-975" data-mp="1ylnwf66amp4f">
                  <BiasCard bias={bias} data-id="element-976" data-mp="1fvy54t1qolh9" />
                </motion.li>)}
            </AnimatePresence>
          </motion.ul>}
      </main>
    </div>;
}